import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconCheck, IconChevronDown, IconX } from '@tabler/icons-react';
import { getDropdownPosition } from './dropdownPosition';
import { useDropdownDismiss } from './useDropdownDismiss';
import './dropdown.css';

/**
 * Type B — searchable select (S2 pattern). When closed, the trigger shows the
 * selected value with a chevron. When clicked, the trigger itself becomes a
 * search input (no separate search zone) and the dropdown attaches flush below.
 *
 * Options are filtered client-side on `label`, so this is for bounded, already
 * loaded lists (Vendor, Technician). For async/remote lookups keep a dedicated
 * search control.
 */
export interface SmartSelectOption {
  value: string;
  label: string;
}

interface SmartSelectProps<T extends SmartSelectOption> {
  label: string;
  value: string | null;
  options: T[];
  onChange: (value: string | null) => void;
  placeholder?: string;
  /** Custom item body (e.g. technician avatar + name). */
  renderItem?: (option: T) => React.ReactNode;
  disabled?: boolean;
}

export function SmartSelect<T extends SmartSelectOption>({
  label,
  value,
  options,
  onChange,
  placeholder = 'Select…',
  renderItem,
  disabled,
}: SmartSelectProps<T>) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const close = React.useCallback(() => {
    setOpen(false);
    setSearch('');
    // Blur so the trigger's :focus-within blue border doesn't linger after the
    // dropdown closes. (The merge chrome — transparent bottom / square corners —
    // is now gated on the --open class, which clears with `open` above.)
    inputRef.current?.blur();
  }, []);

  useDropdownDismiss(dropdownRef, triggerRef, close, open);

  const selectedOption = options.find((o) => o.value === value);
  const filtered = options.filter((o) =>
    o.label.toLowerCase().includes(search.toLowerCase()),
  );

  const handleOpen = () => {
    if (disabled) return;
    setOpen(true);
    // Start with an empty search so the full option list shows; the selected
    // option stays highlighted and the user types to filter. (Pre-filling with
    // the current label filtered the list down to just that one option.)
    setSearch('');
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  return (
    <div className="sm4rt-select" ref={triggerRef}>
      {/* Trigger — becomes a search input when open */}
      <div className={`sm4rt-select__trigger${open ? ' sm4rt-select__trigger--open' : ''}`} onClick={!open ? handleOpen : undefined}>
        <span className="sm4rt-select__trigger-label">{label}</span>

        <input
          ref={inputRef}
          className="sm4rt-select__value"
          value={open ? search : selectedOption?.label ?? ''}
          placeholder={open ? 'Search…' : placeholder}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={handleOpen}
          onKeyDown={(e) => {
            // When the search has narrowed to a single option, Enter selects it.
            if (e.key === 'Enter' && open && filtered.length === 1) {
              e.preventDefault();
              onChange(filtered[0].value);
              close();
            }
          }}
          readOnly={!open}
          disabled={disabled}
        />

        {value && !open && (
          <button
            type="button"
            className="sm4rt-select__clear"
            aria-label="Clear"
            onClick={(e) => {
              e.stopPropagation();
              onChange(null);
            }}
          >
            <IconX size={11} />
          </button>
        )}

        <IconChevronDown
          size={12}
          className={`sm4rt-select__chevron ${open ? 'sm4rt-select__chevron--open' : ''}`}
        />
      </div>

      {open &&
        triggerRef.current &&
        createPortal(
          <div
            ref={dropdownRef}
            className="sm4rt-select__dropdown"
            style={getDropdownPosition(triggerRef.current, { gap: 0 })}
          >
            <div className="sm4rt-dd__list sm4rt-dd__list--scroll">
              {filtered.length > 0 ? (
                filtered.map((option) => (
                  <div
                    key={option.value}
                    className={`sm4rt-dd__item ${
                      option.value === value ? 'sm4rt-dd__item--active' : ''
                    }`}
                    onMouseDown={(e) => {
                      e.preventDefault(); // keep input focus until selection commits
                      onChange(option.value);
                      close();
                    }}
                  >
                    {renderItem?.(option) ?? (
                      <span className="sm4rt-dd__label">{option.label}</span>
                    )}
                    {option.value === value && (
                      <IconCheck className="sm4rt-dd__check" size={12} />
                    )}
                  </div>
                ))
              ) : (
                <div className="sm4rt-dd__empty">No results</div>
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
